import { Stack, ThemeProvider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactFlowProvider } from '@xyflow/react';
import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { DynamicConfig, dynamicConfigUrl } from './config/config';
import { ApplicationProvider } from './context/ApplicationProvider';
import { ViatelAuthProvider } from './context/AuthContext';
import { ClipboardContextProvider } from './context/ClipboardContextProvider';
import { DialogsProvider } from './context/DialogsProvider';
import { SubroutineProvider } from './context/SubroutineProvider';
import { UrlContextProvider } from './context/UrlContextProvider';
import { useConfig } from './hooks/useConfig';
import { useThemeMode } from './hooks/useThemeMode';
import { ErrorView } from './router/ErrorPage';
import Router from './router/Router';
import { useViatelTheme } from './util/theme';

LicenseInfo.setLicenseKey(
  '695abb3b17b05f72579458edabb7da73Tz0xMjk5MjcsRT0xODEyMzI2Mzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1pbml0aWFsLEtWPTI='
);

export default function App() {
  const { config, setConfig } = useConfig();
  const { mode } = useThemeMode();
  const theme = useViatelTheme(config, mode);

  const queryClient = new QueryClient();

  useEffect(() => {
    if (!window.__SENTRY_INITIALIZED__ && config.environment !== 'DEV') {
      console.debug('config app', config);

      Sentry.init({
        dsn: 'https://472b8c667bec485aad18a3b5b3841fb9@o4507222507520000.ingest.de.sentry.io/4507617075920976',
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        enabled: import.meta.env.MODE !== 'development',
        environment: config.environment,
        debug: false,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0
      });
      window.__SENTRY_INITIALIZED__ = true;
    }
  }, [config.environment]);

  const [configLoadingState, setConfigLoadingState] = useState<'loading' | 'ready' | 'error'>('loading');

  /**
   * Make sure console debug is not used live
   */
  const setupConsole = (config: DynamicConfig) => {
    // if (config.environment !== 'PROD') return;
    // console.debug = () => {};
  };

  useEffect(() => {
    fetch(window.location.origin + '/' + dynamicConfigUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error('HTTP error ' + response.status);
        }
        return response.json();
      })
      .then((json) => {
        setupConsole(json);
        setConfig(json);
        setConfigLoadingState('ready');
      })
      .catch((error) => {
        console.error(error);
        setConfigLoadingState('error');
      });
  }, []);

  if (configLoadingState === 'loading') {
    return <p>loading...</p>;
  }
  if (configLoadingState === 'error') {
    return <p>ERROR WHILE FETCHING CONFIG</p>;
  }
  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => <ErrorView error={error} onRetry={resetError} />}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <QueryClientProvider client={queryClient}>
            <DialogsProvider>
              <ViatelAuthProvider>
                <ApplicationProvider>
                  <SubroutineProvider>
                    <ReactFlowProvider>
                      <ClipboardContextProvider>
                        <UrlContextProvider>
                          <Stack direction='column' height='100vh' width='100vw' overflow='hidden'>
                            <RouterProvider router={Router()} />
                          </Stack>
                        </UrlContextProvider>
                      </ClipboardContextProvider>
                    </ReactFlowProvider>
                  </SubroutineProvider>
                </ApplicationProvider>
              </ViatelAuthProvider>
            </DialogsProvider>
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}
